import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCategories, getCategoryById } from "@src/api/mypage/seemore";
import { REGISTRY_IMAGE_URL, REGISTRY_URL, shuffle, isExternalUrl } from "@src/utils";
import ChevronRight from "@src/assets/icons/ChevronRight";
import "./HoneymoonSection.scss";

// 신혼여행 카테고리 ID (운영계: 471, 검증계: 448)
// 환경에 따라 다를 수 있으므로 title로도 찾을 수 있도록 처리
const HONEYMOON_PARENT_IDS = [471, 448]; // 운영계, 검증계
const HONEYMOON_TITLE = "신혼여행";

type HoneymoonCategory = {
  id: number;
  title: string;
  imagePath: string | null;
};

type HoneymoonSectionProps = {
  title?: string;
  description?: string;
};

const HoneymoonSection = ({
  title = "웨딩홀 다음은, 허니문!",
  description = "웨딩홀 안내를 기다리는 동안 가장 설레는 계획을 세워보세요.",
}: HoneymoonSectionProps = {}) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<HoneymoonCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [honeymoonCategoryId, setHoneymoonCategoryId] = useState<number | null>(null);

  useEffect(() => {
    const fetchHoneymoonCategories = async () => {
      try {
        setIsLoading(true);
        
        // 1. 먼저 최상위 카테고리 목록을 가져와서 신혼여행 카테고리 ID 찾기
        let honeymoonCategoryId: number | null = null;
        
        try {
          const categoriesResponse = await getCategories();
          // 응답 구조 확인: response.data.data 또는 response.data
          const categoriesData = (categoriesResponse?.data as any)?.data || (categoriesResponse?.data as any) || [];
          
          if (Array.isArray(categoriesData) && categoriesData.length > 0) {
            // 신혼여행 카테고리 찾기 (운영계: 471, 검증계: 448)
            const honeymoonCategory = categoriesData.find(
              (category: any) => HONEYMOON_PARENT_IDS.includes(category.id || 0)
            ) || categoriesData.find(
              (category: any) => category.title === HONEYMOON_TITLE
            );
            
            if (honeymoonCategory?.id) {
              honeymoonCategoryId = honeymoonCategory.id;
            }
          }
        } catch (error) {
          console.warn("카테고리 목록 조회 실패, ID로 직접 조회 시도:", error);
        }

        // getCategories()에서 찾지 못한 경우, 알려진 ID로 직접 조회 시도
        if (!honeymoonCategoryId) {
          for (const id of HONEYMOON_PARENT_IDS) {
            try {
              const testResponse = await getCategoryById(id, true);
              const testCategory = (testResponse?.data as any)?.data || (testResponse?.data as any);
              if (testCategory && (testCategory.title === HONEYMOON_TITLE || testCategory.id === id)) {
                honeymoonCategoryId = id;
                break;
              }
            } catch (error) {
              // 다음 ID 시도
              continue;
            }
          }
        }

        if (!honeymoonCategoryId) {
          return;
        }

        // 2. 신혼여행 카테고리 상세 조회 (children 포함)
        // 완료 페이지에서는 isEnabledForUser가 false여도 조회 가능하도록 ignoreEnabledForUser: true 전달
        const honeymoonCategoryDetailResponse = await getCategoryById(honeymoonCategoryId, true);
        const honeymoonCategoryDetail = (honeymoonCategoryDetailResponse?.data as any)?.data || (honeymoonCategoryDetailResponse?.data as any);

        // 3. children에서 1차 카테고리 추출
        // 신혼여행 카테고리 ID 저장
        setHoneymoonCategoryId(honeymoonCategoryId);

        if (honeymoonCategoryDetail?.children && honeymoonCategoryDetail.children.length > 0) {
          // 완료 페이지에서는 isEnabledForUser와 관계없이 모든 하위 카테고리 표시
          const firstLevelCategories = honeymoonCategoryDetail.children
            .map((child: any) => ({
              id: child.id || 0,
              title: child.title || "",
              imagePath: child.imagePath || null,
            }));

          if (firstLevelCategories.length > 0) {
            // 랜덤 섞기
            const shuffled = [...firstLevelCategories];
            shuffle(shuffled);
            
            setCategories(shuffled);
          }
        }
      } catch (error) {
        console.error("신혼여행 카테고리 조회 실패:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHoneymoonCategories();
  }, []);

  const handleTitleClick = () => {
    // 신혼여행 카테고리 페이지로 이동 (registry_FE)
    if (honeymoonCategoryId) {
      window.location.href = isExternalUrl(`${REGISTRY_URL}/shop/category/${honeymoonCategoryId}?hidetop=true`);
    } else {
      window.location.href = isExternalUrl(`${REGISTRY_URL}?hidetop=true`);
    }
  };

  const handleCategoryClick = (categoryId: number) => {
    // 카테고리 상세 페이지로 이동 (registry_FE)
    window.location.href = isExternalUrl(`${REGISTRY_URL}/shop/category/${categoryId}?hidetop=true`);
  };

  const getImageUrl = (imagePath: string | null): string | null => {
    if (!imagePath) return null;
    
    // 절대 URL인지 확인
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    return `${REGISTRY_IMAGE_URL}/${imagePath}`;
  };

  if (isLoading || categories.length === 0) {
    return null;
  }

  return (
    <section className="honeymoon-section">
      {/* 구분선 */}
      <div className="honeymoon-divider"></div>
      
      {/* 타이틀 영역 */}
      <div className="honeymoon-title-wrapper" onClick={handleTitleClick}>
        <h2 className="honeymoon-title">{title}</h2>
        <div className="honeymoon-chevron-icon">
          <ChevronRight size={18} color="#191919" />
        </div>
      </div>

      {/* 설명 텍스트 */}
      <p className="honeymoon-description">
        {description}
      </p>

      {/* 카테고리 카드 리스트 */}
      <div className="honeymoon-category-list">
        {categories.map((category) => {
          const imageUrl = getImageUrl(category.imagePath);
          
          return (
            <div
              key={category.id}
              className="honeymoon-category-card"
              onClick={() => handleCategoryClick(category.id)}
            >
              <div className="honeymoon-image-wrapper">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={category.title}
                    className="honeymoon-category-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.parentElement) {
                        target.parentElement.classList.add("no-image");
                      }
                    }}
                  />
                ) : (
                  <div className="honeymoon-no-image-placeholder" />
                )}
              </div>
              <p className="honeymoon-category-name">{category.title}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default HoneymoonSection;
